import {
  AttributeItem,
  JobComplianceDriver,
  ModuleStaticDataState,
  ProductTypeItem,
  ServiceCodeItem,
} from './staticDataTypes';
import { getAttributesList, getDriversList, getProductTypesList, getServiceCodesList } from '@/api';

import { ActionContext } from 'vuex';
import { State } from '@/models/main';
import { sendWarning } from '@/helpers/sentry';
import store from '@/store';

// Driver attributes are those that could be set on Driver level/model in Kraken/OdooV15
// other attributes are set on Job level/model
const driverAttributes = ['TECH1', 'TECH2', 'TECH3'];

export const moduleStaticData = {
  namespaced: true,
  state: {
    attributes: [],
    drivers: [],
    serviceCodes: [],
    productTypes: [],
  },
  getters: {
    getAttributes: (state: ModuleStaticDataState): AttributeItem[] => {
      return state.attributes;
    },
    getDriverAttributes: (state: ModuleStaticDataState): AttributeItem[] => {
      return state.attributes.filter(item => driverAttributes.includes(item.code));
    },
    getJobAttributes: (state: ModuleStaticDataState): AttributeItem[] => {
      return state.attributes.filter(item => !driverAttributes.includes(item.code));
    },
    getDrivers: (state: ModuleStaticDataState): JobComplianceDriver[] => {
      return state.drivers;
    },
    getServiceCodes: (state: ModuleStaticDataState): ServiceCodeItem[] => {
      return state.serviceCodes;
    },
    getProductTypes: (state: ModuleStaticDataState): ProductTypeItem[] => {
      return state.productTypes;
    },
    getAllStaticData: (state: ModuleStaticDataState): ModuleStaticDataState => {
      return state;
    },
  },
  actions: {
    async requestAttributes({ commit }: ActionContext<ModuleStaticDataState, State>): Promise<void> {
      await getAttributesList()
        .then(data => {
          commit('SET_ATTRIBUTES', data);
        })
        .catch(error => {
          sendWarning(error, 'Error when getting the list of attributes');
        });
    },
    async requestDrivers({ commit }: ActionContext<ModuleStaticDataState, State>): Promise<void> {
      await getDriversList()
        .then(data => {
          commit('SET_DRIVERS', data);
        })
        .catch(error => {
          sendWarning(error, 'Error when getting the list of drivers');
        });
    },
    async requestServiceCodes({ commit }: ActionContext<ModuleStaticDataState, State>): Promise<void> {
      await getServiceCodesList()
        .then(data => {
          commit('SET_SERVICE_CODES', data);
        })
        .catch(error => {
          sendWarning(error, 'Error when getting the list of service codes');
        });
    },
    async requestProductTypes({ commit }: ActionContext<ModuleStaticDataState, State>): Promise<void> {
      await getProductTypesList()
        .then(data => {
          commit('SET_PRODUCT_TYPES', data);
        })
        .catch(error => {
          sendWarning(error, 'Error when getting the list of product types');
        });
    },
    requestAllStaticData(): void {
      const stateData = store.getters['moduleStaticData/getAllStaticData'];
      if (Object.keys(stateData).length >= 1) {
        Object.keys(stateData).map(item => {
          if (stateData[item]?.length === 0) {
            const requestName = 'request' + item.charAt(0).toUpperCase() + item.slice(1);
            store.dispatch(`moduleStaticData/${requestName}`);
          }
        });
      }
    },
    async requestAllStaticDataWait(): Promise<void> {
      // Collecting empty list names
      const actions: string[] = [];
      const stateData = store.getters['moduleStaticData/getAllStaticData'];
      if (Object.keys(stateData).length >= 1) {
        Object.keys(stateData).map(item => {
          if (stateData[item]?.length === 0) {
            const requestName = 'request' + item.charAt(0).toUpperCase() + item.slice(1);
            actions.push(requestName);
          }
        });
      }
      // Requesting all the missing lists at the same time
      await Promise.all(
        actions.map(item => {
          return store.dispatch(`moduleStaticData/${item}`);
        }),
      );
    },
  },
  mutations: {
    SET_ATTRIBUTES(state: ModuleStaticDataState, attributes: AttributeItem[]): void {
      state.attributes = attributes;
    },
    SET_DRIVERS(state: ModuleStaticDataState, drivers: JobComplianceDriver[]): void {
      state.drivers = drivers;
    },
    SET_SERVICE_CODES(state: ModuleStaticDataState, codes: ServiceCodeItem[]): void {
      state.serviceCodes = codes;
    },
    SET_PRODUCT_TYPES(state: ModuleStaticDataState, types: ProductTypeItem[]): void {
      state.productTypes = types;
    },
    CLEAR_STATIC_STATE(state: ModuleStaticDataState): void {
      state.attributes = [];
      state.drivers = [];
      state.serviceCodes = [];
      state.productTypes = [];
    },
  },
};
